/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { CatalogScreen } from '../../../catalog/screenExports';
import { MenuOptionGroupsScreen, ProductCombosScreen } from '../../../catalog/public-api';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { getManifestItemsForSurface } from '../navigationManifest';
import { useNavVisibilityContext } from '../useNavVisibilityContext';
import { HubSegmentChips, type HubSegmentChip } from './HubSegmentChips';
import { useCatalogOverlayStore } from '../../../catalog/app-api';

type CatalogSegment = 'products' | 'options' | 'combos';

/** Route params accepted by Catalog for deep-linking to a segment. */
export type CatalogHubRouteParams = {
  initialSegment?: CatalogSegment;
};

/**
 * «Productos» siempre visible (la puerta Catálogo ya filtra por módulo).
 * Opciones de menú y Combos heredan el gate de sus ítems (stackOnly):
 * módulo MenuOptions + capabilities menu_modifiers / product_combos.
 */
const SEGMENT_DEFS: Array<
  HubSegmentChip<CatalogSegment> & { childRoute: string | null }
> = [
  { key: 'products', label: 'Productos', testID: 'catalog-hub.segment.products', childRoute: null },
  { key: 'options', label: 'Opciones de menú', testID: 'catalog-hub.segment.options', childRoute: 'MenuOptionGroups' },
  { key: 'combos', label: 'Combos', testID: 'catalog-hub.segment.combos', childRoute: 'ProductCombos' },
];

/**
 * Catálogo como hub: la puerta sigue siendo «Catálogo» y absorbe Opciones de
 * menú y Combos como segmentos. Monta las pantallas existentes sin tocarlas —
 * cada una conserva su header/hero y sus acciones.
 * Con un solo segmento visible (sin MenuOptions) no se renderiza la fila de
 * chips: es exactamente el Catálogo de siempre.
 */
export function CatalogHubScreen() {
  const route = useRoute<any>();
  const navCtx = useNavVisibilityContext();

  const visibleChips = useMemo(() => {
    const stackItems = getManifestItemsForSurface('stackOnly', navCtx);
    const visibleRoutes = new Set(stackItems.map((item) => item.route));
    return SEGMENT_DEFS.filter(
      (def) => def.childRoute === null || visibleRoutes.has(def.childRoute),
    );
  }, [navCtx]);

  const initialSegmentParam = route.params?.initialSegment as CatalogSegment | undefined;
  const [segment, setSegment] = useState<CatalogSegment>(
    initialSegmentParam ?? 'products',
  );

  useEffect(() => {
    const next = route.params?.initialSegment as CatalogSegment | undefined;
    if (next) {
      setSegment(next);
    }
  }, [route.params?.initialSegment]);

  const pendingHubSegment = useCatalogOverlayStore((s) => s.pendingCatalogHubSegment);
  useEffect(() => {
    if (!pendingHubSegment) return;
    const next = useCatalogOverlayStore.getState().consumeCatalogHubSegmentRequest();
    if (next && visibleChips.some((c) => c.key === next)) {
      setSegment(next);
    }
  }, [pendingHubSegment, visibleChips]);

  const active = visibleChips.some((chip) => chip.key === segment)
    ? segment
    : (visibleChips[0]?.key ?? 'products');

  return (
    <View style={styles.root}>
      {visibleChips.length > 1 ? (
        <HubSegmentChips segments={visibleChips} active={active} onChange={setSegment} />
      ) : null}
      <View style={styles.body}>
        {active === 'products' ? <CatalogScreen /> : null}
        {active === 'options' ? <MenuOptionGroupsScreen /> : null}
        {active === 'combos' ? <ProductCombosScreen /> : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  body: { flex: 1, minHeight: 0 },
});
